/**
 * Step 9: Create accessories for all sections
 *
 * This step creates all accessories defined in botSection.accessories:
 * - Access doors for fan, plenum, and coil (duty_id=1) sections
 * - Openings for any section that has an opening accessory (plenum logic only for last section)
 * - Drain pans for coil sections
 * - Any other accessories added by the user
 */
import { IAccessDoorParams, IBotData, ITunnel } from '../../../../models';
import { SectionCreationContext } from '../../../../models/bot';
import { dispatchQuery } from '../../../../utils/dispatch.utils';
import { ahuApi } from '../../../../services';
import {
  createAccessDoor,
  createOpening,
} from '../../designSections/sectionCreation';

export async function step9CreateAccessories(
  botData: IBotData,
  tunnelIds: number[],
  _tunnels: ITunnel[],
  thickness: number | null,
  casingInsulationId: number | null,
  casingInnerlinerId: number | null,
  floorInnerlinerId: number | null,
  accessDoorParams: IAccessDoorParams,
  getOptions: () => { signal?: AbortSignal },
  onProgress?: (coefficient: number) => void,
  checkStopped?: () => boolean,
): Promise<void> {
  // Process each tunnel sequentially
  for (
    let tunnelIndex = 0;
    tunnelIndex < botData.tunnels.length;
    tunnelIndex++
  ) {
    if (checkStopped?.()) {
      break;
    }
    const botTunnel = botData.tunnels[tunnelIndex];
    const currentTunnelId = tunnelIds[tunnelIndex];
    // Get valid sections for this tunnel
    const validSections = botTunnel.sections.filter(
      (s) => s.type && s.type.trim() !== '',
    );
    if (validSections.length === 0) {
      continue;
    }
    // Get all sections for this tunnel from the API
    const tunnelSections = (await dispatchQuery(
      ahuApi.endpoints.getTunnelSections,
      currentTunnelId,
      getOptions(),
    )) as any[];
    if (!tunnelSections || tunnelSections.length === 0) {
      continue;
    }
    // Create section creation context
    const sectionContext: SectionCreationContext = {
      tunnelId: currentTunnelId,
      tunnelFan: null, // Not needed for accessory creation
      tunnelFanSection: null, // Not needed for accessory creation
      sizerSettings: null, // Not needed for accessory creation
      thickness,
      casingInsulationId,
      casingInnerlinerId,
      floorInnerlinerId,
      validSectionsLength: validSections.length,
      getOptions,
      onProgress: (coeff: number) => onProgress?.(coeff),
      checkStopped: checkStopped || (() => false),
    };
    // Process each section to create accessories
    for (
      let sectionIndex = 0;
      sectionIndex < validSections.length;
      sectionIndex++
    ) {
      if (checkStopped?.()) {
        break;
      }
      const botSection = validSections[sectionIndex];
      // Find the corresponding section from API
      // Try matching by order_rate first (for sections created in step 8)
      // If not found, try matching by type (for fan sections created in step 4)
      let apiSection = tunnelSections.find(
        (s) => s.order_rate === sectionIndex + 1,
      );
      // If not found by order_rate and this is a fan, find by type
      // (fan sections are created in step 4 and may have different order_rate)
      if (!apiSection && botSection.type === 'fan') {
        apiSection = tunnelSections.find((s) => s.type === 'fan');
      }
      if (!apiSection) {
        continue;
      }
      // Determine if this is the last section
      // const isLastSection = sectionIndex === validSections.length - 1;
      // Create all accessories defined in botSection.accessories
      // Loop through each accessory and create it based on type
      if (botSection.accessories && botSection.accessories.length > 0) {
        for (const accessory of botSection.accessories) {
          if (checkStopped?.()) {
            break;
          }
          try {
            switch (accessory.type) {
              case 'access_door':
                await createAccessDoor(
                  botSection,
                  apiSection,
                  accessory,
                  sectionContext,
                  accessDoorParams.min_width,
                );
                break;
              case 'opening':
                await createOpening(
                  botSection,
                  apiSection,
                  accessory,
                  sectionContext,
                  botData,
                );
                break;
              case 'drain_pan':
                //await createDrainPan(apiSection, accessory, sectionContext);
                break;
              default:
                // For other accessory types, log that they are not yet supported
                // or handle them if they have create endpoints
                console.warn(
                  `Accessory type "${accessory.type}" is not yet supported for automatic creation in step 9`,
                );
                break;
            }
          } catch (error) {
            console.error(
              `Failed to create ${accessory.type} accessory for section ${apiSection.id}:`,
              error,
            );
          }
        }
      }
    }
  }
}
